import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';
import { NotificationDto } from '../types/notifications';
import { useAuthContext } from './AuthContext';
import { useToast } from './ToastContext';
import { ensureValidAccessToken } from '../lib/auth/ensureValidAccessToken';
import { authAwareRetryPolicy } from '../lib/realtime/authAwareRetryPolicy';
import { createUnreadLedger } from './notificationUnreadLedger';
import * as signalR from '@microsoft/signalr';

interface NotificationsContextType {
    notifications: NotificationDto[];
    unreadCount: number;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    markAsRead: (id: number) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    deleteNotification: (id: number) => Promise<void>;
    clearAll: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

const SIGNALR_HUB_URL = process.env.REACT_APP_SIGNALR_HUB_URL || 'https://devanza-dev-backend.azurewebsites.net/hubs/notifications';

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<NotificationDto[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { accessToken, isAuthenticated } = useAuthContext();
    const { showToast } = useToast();
    const connectionRef = useRef<signalR.HubConnection | null>(null);
    // AB#2139 — the REST response, the server's echo to this user's group and a
    // second connection during a token refresh can all report the same change;
    // the ledger lets each notification move the unread count only once.
    const [ledger] = useState(createUnreadLedger);

    const getUserProfileId = useCallback(() => {
        if (!accessToken) return null;
        try {
            const payload = accessToken.split('.')[1];
            const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
            return decoded.userProfileId ? parseInt(decoded.userProfileId) : null;
        } catch {
            return null;
        }
    }, [accessToken]);

    const refresh = useCallback(async () => {
        if (!isAuthenticated) return;
        // AB#1434.b — Auth v2 identity-only tokens (issued when a multi-profile
        // user needs to pick via the post-login chooser) carry no `userProfileId`
        // claim. /api/notifications requires one, so the call would 401 and
        // the axios interceptor would wipe the (valid) identity token,
        // stranding the user on the login page. Skip the fetch until the
        // chooser upgrades the token with `switchProfileV2`.
        if (getUserProfileId() == null) return;

        setLoading(true);
        setError(null);

        try {
            const response = await apiService.getNotifications();
            ledger.seed(response.notifications);
            setNotifications(response.notifications);
            setUnreadCount(response.unreadCount);
        } catch (err) {
            setError('Unable to load notifications. Please refresh or check back later.');
            console.error('Failed to fetch notifications:', err);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, getUserProfileId, ledger]);

    // Every read / delete — this tab's own or the server's echo — goes through these.
    const applyRead = useCallback((id: number) => {
        const change = ledger.read(id);
        setNotifications(prev =>
            prev.map(n => n.id === id ? { ...n, isRead: true } : n)
        );
        if (change) setUnreadCount(prev => Math.max(0, prev + change));
    }, [ledger]);

    const applyReadAll = useCallback(() => {
        ledger.readAll();
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
    }, [ledger]);

    const applyRemove = useCallback((id: number) => {
        const change = ledger.remove(id);
        setNotifications(prev => prev.filter(n => n.id !== id));
        if (change) setUnreadCount(prev => Math.max(0, prev + change));
    }, [ledger]);

    const applyClearAll = useCallback(() => {
        ledger.clearAll();
        setNotifications([]);
        setUnreadCount(0);
    }, [ledger]);

    const markAsRead = useCallback(async (id: number) => {
        try {
            await apiService.markNotificationAsRead(id);
            applyRead(id);
        } catch (err) {
            showToast('Unable to mark notification as read. Please try again.', 'error');
        }
    }, [showToast, applyRead]);

    const markAllAsRead = useCallback(async () => {
        try {
            await apiService.markAllNotificationsAsRead();
            applyReadAll();
        } catch (err) {
            showToast('Unable to mark all notifications as read. Please try again.', 'error');
        }
    }, [showToast, applyReadAll]);

    const deleteNotification = useCallback(async (id: number) => {
        try {
            await apiService.deleteNotification(id);
            applyRemove(id);
        } catch (err) {
            showToast('Unable to clear the notification. Please try again.', 'error');
        }
    }, [showToast, applyRemove]);

    const clearAll = useCallback(async () => {
        try {
            await apiService.clearAllNotifications();
            applyClearAll();
        } catch (err) {
            showToast('Unable to clear all notifications. Please try again.', 'error');
        }
    }, [showToast, applyClearAll]);

    useEffect(() => {
        if (isAuthenticated && 'Notification' in window) {
            if (Notification.permission === 'default') {
                Notification.requestPermission();
            }
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            refresh();
        }
    }, [isAuthenticated, refresh]);

    useEffect(() => {
        if (!isAuthenticated || !accessToken) return;

        const userProfileId = getUserProfileId();
        if (!userProfileId) return;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(SIGNALR_HUB_URL, {
                // AB#1470 — read the token from localStorage on every call
                // (not the captured React state) and refresh it if expired.
                // This keeps SignalR in sync with the axios interceptor's
                // silent refresh flow and avoids a 401 reconnect loop after
                // the access token expires.
                accessTokenFactory: () => ensureValidAccessToken(),
            })
            // S1 (AB#2204, SG-11) — stop reconnecting once the session is refused
            // (401 / 403, e.g. a staff member whose access was removed) or gone.
            .withAutomaticReconnect(authAwareRetryPolicy)
            .build();

        connection.on('OnNewNotification', (notification: NotificationDto) => {
            const change = ledger.arrive(notification);
            if (change === null) return; // already received, e.g. on the connection being replaced
            setNotifications(prev => [notification, ...prev]);
            if (change) setUnreadCount(prev => prev + change);

            if ('Notification' in window && Notification.permission === 'granted') {
                try {
                    new Notification(notification.title, {
                        body: notification.message || '',
                        icon: '/assets/icons/layout/logo.png',
                        tag: `notification-${notification.id}`
                    });
                } catch (e) {
                    console.error('Failed to show browser notification:', e);
                }
            }
        });

        connection.on('OnNotificationRead', applyRead);
        connection.on('OnAllNotificationsRead', applyReadAll);
        connection.on('OnNotificationDeleted', applyRemove);
        connection.on('OnAllNotificationsCleared', applyClearAll);

        let isCancelled = false;

        connection.start()
            .then(() => {
                if (!isCancelled) {
                    connection.invoke('JoinUserGroup', userProfileId)
                        .catch((err: Error) => console.error('Failed to join notification group:', err));
                }
            })
            .catch((err: Error) => {
                if (!isCancelled) {
                    console.error('SignalR Connection Error:', err);
                }
            });

        connectionRef.current = connection;

        return () => {
            isCancelled = true;
            if (connectionRef.current) {
                connectionRef.current.invoke('LeaveUserGroup', userProfileId).catch(() => { });
                connectionRef.current.stop();
                connectionRef.current = null;
            }
        };
    }, [isAuthenticated, accessToken, getUserProfileId, ledger, applyRead, applyReadAll, applyRemove, applyClearAll]);

    return (
        <NotificationsContext.Provider value={{
            notifications,
            unreadCount,
            loading,
            error,
            refresh,
            markAsRead,
            markAllAsRead,
            deleteNotification,
            clearAll
        }}>
            {children}
        </NotificationsContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationsProvider');
    }
    return context;
};
